// ResizeObserver 错误修复工具
// 解决浏览器缩放时出现的 ResizeObserver 警告

// 检查 ResizeObserver 是否可用
if (!window.ResizeObserver) {
  console.warn('ResizeObserver is not supported in this browser');
}

// 保存原始的 ResizeObserver
const OriginalResizeObserver = window.ResizeObserver;

// 创建防抖函数
const debounce = (func: (...args: any[]) => void, wait: number) => {
  let timeout: number;
  return function executedFunction(...args: any[]) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = window.setTimeout(later, wait);
  };
};

// 安全地处理 ResizeObserverEntry
const safeProcessEntries = (entries: ResizeObserverEntry[]) => {
  return entries.filter(entry => {
    try {
      // 检查 entry.target 是否存在且有效
      if (!entry.target || typeof entry.target.getBoundingClientRect !== 'function') {
        return false;
      }
      // 尝试调用 getBoundingClientRect 来验证元素是否有效
      entry.target.getBoundingClientRect();
      return true;
    } catch (error) {
      // 如果元素无效，过滤掉这个 entry
      return false;
    }
  });
};

// 重写 ResizeObserver 构造函数
class SafeResizeObserver extends OriginalResizeObserver {
  constructor(callback: ResizeObserverCallback) {
    // 创建防抖的回调函数
    const debouncedCallback = debounce((entries: ResizeObserverEntry[], observer: ResizeObserver) => {
      try {
        // 安全地处理 entries
        const safeEntries = safeProcessEntries(entries);
        if (safeEntries.length > 0) {
          callback(safeEntries, observer);
        }
      } catch (error) {
        // 静默处理 ResizeObserver 相关错误
        if (error instanceof Error && (
          error.message.includes('ResizeObserver') ||
          error.message.includes('getBoundingClientRect') ||
          error.message.includes('Cannot read properties')
        )) {
          return;
        }
        throw error;
      }
    }, 16); // 16ms 防抖，约等于 60fps

    super(debouncedCallback);
  }
}

// 替换全局 ResizeObserver
(window as any).ResizeObserver = SafeResizeObserver;

// 重写 console 方法来过滤 ResizeObserver 警告
const originalError = console.error;
const originalWarn = console.warn;

console.error = (...args: any[]) => {
  const message = args[0];
  if (typeof message === 'string' && (
    message.includes('ResizeObserver') ||
    message.includes('ResizeObserver loop completed with undelivered notifications') ||
    message.includes('ResizeObserver loop limit exceeded') ||
    message.includes('getBoundingClientRect is not a function') ||
    message.includes('Cannot read properties')
  )) {
    return;
  }
  originalError.apply(console, args);
};

console.warn = (...args: any[]) => {
  const message = args[0];
  if (typeof message === 'string' && (
    message.includes('ResizeObserver') ||
    message.includes('ResizeObserver loop completed with undelivered notifications') ||
    message.includes('ResizeObserver loop limit exceeded') ||
    message.includes('getBoundingClientRect is not a function') ||
    message.includes('Cannot read properties') ||
    message.includes('Runtime directive used on component with non-element root node') ||
    message.includes('The directives will not function as intended') ||
    message.includes('Invalid prop: custom validator check failed for prop "role"') ||
    message.includes('Invalid prop: custom validator check failed') ||
    message.includes('Invalid prop: validation failed for prop "role"') ||
    message.includes('label act as value is about to be deprecated') ||
    message.includes('type.text is about to be deprecated') ||
    message.includes('please use link instead') ||
    message.includes('please use value instead') ||
    message.includes('ElementPlusError') ||
    message.includes('Expected one of') ||
    message.includes('got value') ||
    message.includes('[props] [API]') ||
    message.includes('is about to be deprecated in version 3.0.0') ||
    message.includes('button.html#button-attributes') ||
    message.includes('checkbox.html') ||
    message.includes('For more detail, please visit:') ||
    message.includes('[el-checkbox] [API]') ||
    message.includes('label act as value') ||
    message.includes('please use value instead')
  )) {
    return;
  }
  originalWarn.apply(console, args);
};

// 全局错误处理
window.addEventListener('error', (event) => {
  if (event.message && (
    event.message.includes('ResizeObserver') ||
    event.message.includes('ResizeObserver loop completed with undelivered notifications') ||
    event.message.includes('ResizeObserver loop limit exceeded') ||
    event.message.includes('getBoundingClientRect is not a function') ||
    event.message.includes('Cannot read properties') ||
    event.message.includes('Runtime directive used on component with non-element root node') ||
    event.message.includes('The directives will not function as intended') ||
    event.message.includes('Invalid prop: custom validator check failed for prop "role"') ||
    event.message.includes('Invalid prop: custom validator check failed') ||
    event.message.includes('Invalid prop: validation failed for prop "role"') ||
    event.message.includes('label act as value is about to be deprecated') ||
    event.message.includes('type.text is about to be deprecated') ||
    event.message.includes('please use link instead') ||
    event.message.includes('please use value instead') ||
    event.message.includes('ElementPlusError') ||
    event.message.includes('Expected one of') ||
    event.message.includes('got value') ||
    event.message.includes('[props] [API]') ||
    event.message.includes('is about to be deprecated in version 3.0.0') ||
    event.message.includes('button.html#button-attributes') ||
    event.message.includes('checkbox.html') ||
    event.message.includes('For more detail, please visit:') ||
    event.message.includes('[el-checkbox] [API]') ||
    event.message.includes('label act as value') ||
    event.message.includes('please use value instead')
  )) {
    event.preventDefault();
    return false;
  }
});

// 全局未捕获Promise错误处理
window.addEventListener('unhandledrejection', (event) => {
  if (event.reason && typeof event.reason === 'object' && 'message' in event.reason) {
    const message = (event.reason as any).message;
    if (typeof message === 'string' && (
      message.includes('ResizeObserver') ||
      message.includes('ResizeObserver loop completed with undelivered notifications') ||
      message.includes('ResizeObserver loop limit exceeded') ||
      message.includes('getBoundingClientRect is not a function') ||
      message.includes('Cannot read properties') ||
      message.includes('Runtime directive used on component with non-element root node') ||
      message.includes('The directives will not function as intended') ||
      message.includes('Invalid prop: custom validator check failed for prop "role"') ||
      message.includes('Invalid prop: custom validator check failed') ||
      message.includes('Invalid prop: validation failed for prop "role"') ||
      message.includes('label act as value is about to be deprecated') ||
      message.includes('type.text is about to be deprecated') ||
      message.includes('please use link instead') ||
      message.includes('please use value instead') ||
      message.includes('ElementPlusError') ||
      message.includes('Expected one of') ||
      message.includes('got value') ||
      message.includes('[props] [API]') ||
      message.includes('is about to be deprecated in version 3.0.0') ||
      message.includes('button.html#button-attributes') ||
      message.includes('checkbox.html') ||
      message.includes('For more detail, please visit:') ||
      message.includes('[el-checkbox] [API]') ||
      message.includes('label act as value') ||
      message.includes('please use value instead')
    )) {
      event.preventDefault();
      return false;
    }
  }
});

export default SafeResizeObserver;


